import { Component, OnDestroy, OnInit } from '@angular/core'; // Add OnInit here
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { PayrollService } from '../../../shared/services/payroll.service';
import { QeqService } from '../../../shared/services/qeq/qeq.service';
import { Subscription, interval } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ViewChild, inject } from '@angular/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { QeqPipe } from 'src/app/shared/pipes/qeq-pipe/qeq-pippe.pipe';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-upload-payroll',
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    CommonModule,
    MatDialogModule,
    MatMenuModule,
    MatPaginatorModule,
    MatTabsModule,
    MatDatepickerModule,
    MatInputModule,
    MatSlideToggleModule,
    TitleComponent,
    LoaderComponent,
    MatProgressBarModule,
    MatIconModule,
    QeqPipe,
  ],
  templateUrl: './upload-payroll.component.html',
  styleUrls: [
    './upload-payroll.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class UploadPayrollComponent implements OnDestroy, OnInit {
  title: string = 'Nóminas';
  subtitle: string = 'Gestiona la subida de las nóminas.';
  isUuid: boolean = false;

  selectedMonth: string = '';
  selectedYear: string = '';
  selectedCompany: string = '';
  searchTerm: string = '';
  payrollFiles: string[] = [];
  availableYears: number[] = [];
  filesDataSource: MatTableDataSource<string> =
    new MatTableDataSource<string>();
  displayedColumns: string[] = [
    'filename',
    'type',
    'status',
    // 'acciones'
  ];
  checkFilesResult: any = null;

  // Flags to control when to hide the loader after parallel requests
  private loadPayrollCompleted: boolean = false;
  private checkPayrollCompleted: boolean = false;

  // Valid companies
  readonly validCompanies: Company[] = ['serveto_svt', 'serveto_xofers_lux', 'serveto_gestio_lux', 'serveto_sas'];

  // Processing status per company
  processingStatus: { [company in Company]?: PayrollProcessStatus | null } = {};
  // Single polling subscription
  private pollingSubscription: Subscription | null = null;
  // Polling subscription for latest processes
  private latestProcessesPollingSubscription: Subscription | null = null;

  // Completed / fetched pages per company
  processedPages: { [company in Company]?: PayrollProcessPage[] } = {};
  pagesDisplayedColumns: string[] = [
    'nro',
    'personnel_number',
    'month',
    'year',
    'company',
  ];
  pagesDataSource: MatTableDataSource<PayrollProcessPageWithCompany> =
    new MatTableDataSource<PayrollProcessPageWithCompany>([]);

  // Employee payroll properties
  employeePayrollData: EmployeePayrollData[] = [];
  employeePayrollDataSource: MatTableDataSource<EmployeePayrollData> =
    new MatTableDataSource<EmployeePayrollData>([]);
  employeePayrollDisplayedColumns: string[] = [
    'user',
    'personnel_number',
    'nro',
    'year',
    'month',
    'company',
    'total_pages',
    'validated',
    'validate',
  ];

  // Track which employee payroll is being updated to prevent multiple simultaneous updates
  updatingValidationIds: Set<number> = new Set();

  // Latest processes data for each company
  latestProcessesData: any = null;

  private subscriptions: Subscription[] = [];
  private _liveAnnouncer = inject(LiveAnnouncer);

  @ViewChild(MatSort)
  sort: MatSort = new MatSort();

  constructor(
    private loaderService: LoaderService,
    private payrollService: PayrollService,
    private qeqService: QeqService,
    private snackBar: MatSnackBar
  ) {}

  get isLoading() {
    return this.loaderService.getLoader();
  }

  cancelPayroll() {
    this.payrollService.cancelPayroll().subscribe({
      next: (response: any) => {
        console.log('Cancel response:', response);
      },
      error: (error: any) => {
        console.error('Error cancelling payroll:', error);
      },
    });
  }

  getEmployeePayroll() {
    const year = parseInt(this.selectedYear);
    const month = parseInt(this.selectedMonth);

    const subscription = this.payrollService
      .getEmployeePayroll(year, month)
      .subscribe({
        next: (response: any) => {
          console.log('Employee payroll data:', response);
          this.employeePayrollData = response.results;
          this.employeePayrollDataSource.data = this.employeePayrollData;
          this.employeePayrollDataSource.sort = this.sort;
        },
        error: (error: any) => {
          console.error('Error fetching employee payroll:', error);
          this.employeePayrollData = [];
          this.employeePayrollDataSource.data = [];
        },
      });

    this.subscriptions.push(subscription);
  }

  ngAfterViewInit() {}

  ngOnInit() {
    this.initializeYears();
    this.setCurrentMonthYear();
    this.startLoadingData();
    this.startLatestProcessesPolling();

    // Configure filter predicate so the pages table can be filtered by nro or personnel_number
    this.pagesDataSource.filterPredicate = (
      data: PayrollProcessPageWithCompany,
      filter: string
    ) => {
      const normalized = filter.trim().toLowerCase();
      const nro = data.nro ? data.nro.toString().toLowerCase() : '';
      const personnel = data.personnel_number
        ? data.personnel_number.toString().toLowerCase()
        : '';
      return nro.includes(normalized) || personnel.includes(normalized);
    };

    // Configure filter predicate for employee payroll table
    this.employeePayrollDataSource.filterPredicate = (
      data: EmployeePayrollData,
      filter: string
    ) => {
      const filterData = JSON.parse(filter);
      const { company, search } = filterData;

      // Filter by company if selected
      const companyMatches = !company || data.company === company;

      // Filter by search term (personnel number or user name via qeqPipe)
      let searchMatches = true;
      if (search) {
        const searchLower = search.toLowerCase();
        const personnelMatches = data.personnel_number
          .toLowerCase()
          .includes(searchLower);

        // Get user name via QeQ service for name matching
        const qeqData = this.qeqService['getQeqFromStorage']?.();
        let nameMatches = false;
        if (qeqData?.data) {
          const user = qeqData.data.find(
            (u: any) => u.personnelnumber === data.personnel_number
          );
          nameMatches = user
            ? user.name.toLowerCase().includes(searchLower)
            : false;
        }

        searchMatches = personnelMatches || nameMatches;
      }

      return companyMatches && searchMatches;
    };

    // Configure filter predicate for files table (filter by filename)
    this.filesDataSource.filterPredicate = (data: string, filter: string) => {
      const normalized = filter.trim().toLowerCase();
      return data.toString().toLowerCase().includes(normalized);
    };
  }

  private initializeYears() {
    const currentYear = new Date().getFullYear();
    this.availableYears = [];
    for (let i = 0; i < 5; i++) {
      this.availableYears.push(currentYear - i);
    }
  }

  private setCurrentMonthYear() {
    const currentDate = new Date();
    this.selectedMonth = (currentDate.getMonth() + 1).toString(); // getMonth() returns 0-11, so add 1
    this.selectedYear = currentDate.getFullYear().toString();
  }

  private startLatestProcessesPolling() {
    // Poll every 5 seconds
    this.latestProcessesPollingSubscription = interval(5000).subscribe(() => {
      this.fetchLatestProcesses();
    });

    // Execute immediately on start
    this.fetchLatestProcesses();
  }

  private fetchLatestProcesses() {
    this.payrollService.getLatestProcesses(false).subscribe({
      next: (response: any) => {
        this.latestProcessesData = response;
        console.log('Latest processes:', response);
      },
      error: (error: any) => {
        console.error('Error fetching latest processes:', error);
        this.latestProcessesData = null;

        // Show toast with error message
        const errorMessage =
          error.error?.error || error.message || 'Error desconocido';
        // this.snackBar.open(
        //   `Error obteniendo últimos procesos: ${errorMessage}`,
        //   'Cerrar',
        //   {
        //     duration: 5000,
        //   }
        // );
      },
    });
  }

  clearSubscriptions() {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });

    // Reset the subscriptions array
    this.subscriptions = [];
  }

  // Ensure all subscriptions are cleared
  ngOnDestroy(): void {
    // Stop polling
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }

    // Stop latest processes polling
    if (this.latestProcessesPollingSubscription) {
      this.latestProcessesPollingSubscription.unsubscribe();
    }

    // Clean up all other subscriptions
    this.clearSubscriptions();

    // Clear updating validation IDs
    this.updatingValidationIds.clear();
  }

  onMonthYearChange() {
    if (this.selectedMonth && this.selectedYear) {
      // Start loading other payroll-related data for the selected month/year
      this.startLoadingData();

      // Also automatically fetch employee payroll for the selected month/year
      // replacing the need for the manual "Cargar Datos" button
      this.getEmployeePayroll();
    }
  }

  onFiltersChange() {
    // Apply filters to the employee payroll data source
    const filterValue = JSON.stringify({
      company: this.selectedCompany,
      search: this.searchTerm,
    });
    this.employeePayrollDataSource.filter = filterValue;
  }

  // Central method to start both API calls and manage loader state
  private startLoadingData() {
    // Reset flags
    this.loadPayrollCompleted = false;
    this.checkPayrollCompleted = false;

    // Show loader once before starting parallel requests
    this.loaderService.showLoader();

    // Trigger both requests in parallel
    //this.loadPayrollFiles();
    this.checkPayrollFiles();
  }

  private tryHideLoader() {
    this.loaderService.hideLoader();
  }

  private checkPayrollFiles() {
    const month = parseInt(this.selectedMonth);
    const year = parseInt(this.selectedYear);

    this.payrollService.checkPayrollFiles(month, year).subscribe({
      next: (response: any) => {
        console.log('Check files result:', response);
        this.checkFilesResult = response;
        this.checkPayrollCompleted = true;
        this.tryHideLoader();
      },
      error: (error: any) => {
        console.error('Error checking payroll files:', error);
        this.checkFilesResult = null;
        this.checkPayrollCompleted = true; // Even on error we consider the call finished
        this.tryHideLoader();
      },
    });
  }

  processNomines(company: 'serveto_svt'| 'serveto_xofers_lux' | 'serveto_gestio_lux' | 'serveto_sas') {
    if (this.selectedMonth && this.selectedYear) {
      const month = parseInt(this.selectedMonth);
      const year = parseInt(this.selectedYear);

      const subscription = this.payrollService
        .extractNroByMonthYear(month, year, company)
        .subscribe({
          next: (response: any) => {
            console.log('Process initiated:', response);
          },
          error: (error: any) => {
            console.error('Error processing nomines:', error);
          },
        });

      this.subscriptions.push(subscription);
    }
  }

  // Determine if a company's process button should be disabled
  isCompanyProcessing(company: Company): boolean {
    const status = this.processingStatus[company];
    const inProgress = !!status && ['IN_PROGRESS'].includes(status.status);
    return inProgress;
  }

  private refreshPagesDataSource() {
    const combined: PayrollProcessPageWithCompany[] = [];
    (this.validCompanies as Company[]).forEach((company) => {
      const pages = this.processedPages[company];
      if (pages && pages.length) {
        pages.forEach((p) => combined.push({ ...p, company }));
      }
    });
    this.pagesDataSource.data = combined;
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    } else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
  }

  downloadFile(filename: string) {
    console.log('Download file:', filename);
    // TODO: Implement file download functionality
  }

  // Method to handle validation checkbox changes
  onValidationChange(employeePayroll: EmployeePayrollData, event: any) {
    const newValidatedStatus = event.checked;

    // Prevent multiple simultaneous updates for the same employee
    if (this.updatingValidationIds.has(employeePayroll.id)) {
      console.log(
        'Update already in progress for employee:',
        employeePayroll.personnel_number
      );
      return;
    }

    // Add to updating set
    this.updatingValidationIds.add(employeePayroll.id);

    // Prepare the data to send to the API
    const validationData = {
      validated: newValidatedStatus,
    };

    // Call the API to persist the validation status
    const subscription = this.payrollService
      .validateEmployeePayroll(employeePayroll.id, validationData)
      .subscribe({
        next: (response: any) => {
          console.log('Validation updated successfully:', response);

          // Update the local data only after successful API call
          employeePayroll.validated = newValidatedStatus;
          if (newValidatedStatus) {
            employeePayroll.validated_date = new Date().toISOString();
          } else {
            employeePayroll.validated_date = null;
          }

          // Refresh the data source to update the UI
          this.employeePayrollDataSource.data = [...this.employeePayrollData];

          // Remove from updating set
          this.updatingValidationIds.delete(employeePayroll.id);
        },
        error: (error: any) => {
          console.error('Error updating validation status:', error);

          // Remove from updating set
          this.updatingValidationIds.delete(employeePayroll.id);

          // Revert the toggle state since the API call failed
          // We need to trigger change detection manually
          setTimeout(() => {
            employeePayroll.validated = !newValidatedStatus;
            this.employeePayrollDataSource.data = [...this.employeePayrollData];
          }, 0);

          // TODO: Show user-friendly error message
          alert(
            'Error al actualizar el estado de validación. Por favor, inténtalo de nuevo.'
          );
        },
      });

    this.subscriptions.push(subscription);
  }

  // Helper method to check if validation is being updated
  isValidationUpdating(employeePayrollId: number): boolean {
    return this.updatingValidationIds.has(employeePayrollId);
  }

  // Helper method to get month name
  getMonthName(month: string): string {
    const monthNames = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre',
    ];
    const monthNumber = parseInt(month);
    return monthNames[monthNumber - 1] || '';
  }

  /**
   * Change validation status for all employee payrolls
   * @param validate - true to validate all, false to unvalidate all
   */
  changeAllValidationStatus(validate: boolean) {
    if (this.employeePayrollData.length === 0) {
      return;
    }

    // Filter items based on the action: validate (get unvalidated) or unvalidate (get validated)
    const idsToUpdate = this.employeePayrollData
      .filter((ep) => (validate ? !ep.validated : ep.validated))
      .map((ep) => ep.id);

    if (idsToUpdate.length === 0) {
      // Show friendly message when no items need to be updated
      const message = validate
        ? 'Todas las nóminas ya están validadas'
        : 'Todas las nóminas ya están invalidadas';
      this.snackBar.open(message, 'Cerrar', { duration: 3000 });
      return;
    }

    // Add all IDs to updating set
    idsToUpdate.forEach((id) => this.updatingValidationIds.add(id));

    // Call the endpoint with the appropriate validation status
    const subscription = this.payrollService
      .changeStatusEmployeePayroll(idsToUpdate, validate)
      .subscribe({
        next: (response: any) => {
          // Update all local items with the new validation status
          this.employeePayrollData.forEach((ep) => {
            if (idsToUpdate.includes(ep.id)) {
              ep.validated = validate;
              ep.validated_date = validate ? new Date().toISOString() : null;
            }
          });

          // Refresh the data source to update the UI
          this.employeePayrollDataSource.data = [...this.employeePayrollData];

          // Remove ids from updating set
          idsToUpdate.forEach((id) => this.updatingValidationIds.delete(id));

          // Show success message
          const successMessage = validate
            ? `${idsToUpdate.length} nóminas validadas correctamente`
            : `${idsToUpdate.length} nóminas invalidadas correctamente`;
          this.snackBar.open(successMessage, 'Cerrar', { duration: 3000 });
        },
        error: (error: any) => {
          console.error(
            `Error ${
              validate ? 'validating' : 'unvalidating'
            } employee payrolls:`,
            error
          );

          // Remove ids from updating set so user can retry
          idsToUpdate.forEach((id) => this.updatingValidationIds.delete(id));

          // Show user-friendly error message
          const action = validate ? 'validar' : 'invalidar';
          const msg =
            error?.error?.message ||
            error?.message ||
            `Error al ${action} nóminas`;
          this.snackBar.open(msg, 'Cerrar', { duration: 5000 });
        },
      });

    this.subscriptions.push(subscription);
  }

  // Convenience methods for template usage
  validateAll() {
    this.changeAllValidationStatus(true);
  }

  unvalidateAll() {
    this.changeAllValidationStatus(false);
  }
}

// Types
type Company = 'serveto_svt' | 'serveto_xofers_lux' | 'serveto_gestio_lux' | 'serveto_sas';

interface PayrollProcessStatus {
  id: number;
  start_time: string;
  end_time: string | null;
  current_page: number;
  total_pages: number;
  company: Company;
  month: number;
  year: number;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'ERROR' | string;
  error_message: string;
  duration: string | null;
  progress_percentage: number | null;
  pages?: PayrollProcessPage[];
}

interface PayrollProcessPage {
  id: number;
  page_number: number;
  nro: string;
  personnel_number: string;
  date: string;
  filename: string;
  file_path: string;
  month: number;
  year: number;
  extracted_at?: string;
  updated_at?: string;
}

interface PayrollProcessPageWithCompany extends PayrollProcessPage {
  company: Company;
}

interface EmployeePayrollData {
  id: number;
  employee_id: number;
  personnel_number: string;
  company: string;
  nro: string;
  year: number;
  month: number;
  total_pages: number;
  validated: boolean;
  validated_date: string | null;
  payroll_process_id: number;
  created_at: string;
}
